import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Eye } from 'lucide-react';
import { ProfileTemplate } from '@/services/profileTemplateService';
import { cn } from '@/lib/utils';

interface Props {
  template: ProfileTemplate;
  isSelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
  selectionDisabled?: boolean;
  selectionDescriptionId?: string;
}

function TemplateArtwork({ templateId, imageUrl }: { templateId: string; imageUrl: string | null }) {
  if (imageUrl) {
    return <img src={imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />;
  }

  if (templateId === 'tech-1') {
    return (
      <div className="h-full bg-[#070b16] p-2 text-white">
        <div className="flex items-center justify-between"><div className="h-1.5 w-12 rounded bg-cyan-300/70" /><div className="h-1.5 w-8 rounded bg-white/20" /></div>
        <div className="mt-2 grid grid-cols-[1.2fr_0.8fr] gap-2">
          <div><div className="h-1.5 w-14 rounded bg-cyan-300/50" /><div className="mt-1.5 h-2.5 w-full rounded bg-white/90" /><div className="mt-1 h-2.5 w-4/5 rounded bg-white/90" /><div className="mt-1.5 h-2 w-12 rounded bg-cyan-300" /></div>
          <div className="aspect-[4/5] rounded border border-cyan-300/30 bg-gradient-to-br from-cyan-300/20 to-indigo-400/30" />
        </div>
      </div>
    );
  }

  if (templateId === 'minimal-1') {
    return (
      <div className="h-full bg-[#fbfaf7] p-2 text-[#22201d]">
        <div className="flex items-center justify-between"><div className="h-1.5 w-12 rounded bg-[#22201d]/70" /><div className="h-1.5 w-8 rounded bg-[#22201d]/20" /></div>
        <div className="mt-2 grid grid-cols-[1.25fr_0.75fr] items-center gap-2">
          <div><div className="h-1.5 w-12 rounded bg-[#9a6b38]/60" /><div className="mt-1.5 h-2.5 w-full rounded bg-[#22201d]/85" /><div className="mt-1 h-2.5 w-3/4 rounded bg-[#22201d]/85" /><div className="mt-1.5 h-2 w-10 rounded-full bg-[#22201d]" /></div>
          <div className="aspect-[3/4] rounded-t-full rounded-b bg-[#e8e2d7]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#f7f5f0] p-2 text-slate-950">
      <div className="flex items-center justify-between"><div className="h-1.5 w-14 rounded bg-slate-900/70" /><div className="h-2 w-10 rounded-full bg-slate-900" /></div>
      <div className="mt-2 grid grid-cols-[1.15fr_0.85fr] items-center gap-2">
        <div><div className="h-1.5 w-10 rounded bg-primary/60" /><div className="mt-1.5 h-2.5 w-full rounded bg-slate-900/90" /><div className="mt-1 h-2.5 w-5/6 rounded bg-slate-900/90" /><div className="mt-1.5 h-2 w-12 rounded-full bg-primary" /></div>
        <div className="aspect-[4/5] rounded bg-slate-300 shadow-sm" />
      </div>
    </div>
  );
}

export const TemplateCard: React.FC<Props> = ({
  template,
  isSelected,
  onSelect,
  onPreview,
  selectionDisabled = false,
  selectionDescriptionId
}) => {
  return (
    <Card className={cn(
      'website-template-card',
      isSelected && 'website-template-card--selected'
    )} data-selected={isSelected}>
      <button
        type="button"
        onClick={onPreview}
        className="website-template-card__artwork"
        aria-label={`Preview ${template.name} template`}
      >
        <TemplateArtwork templateId={template.id} imageUrl={template.preview_image_url} />
      </button>

      <CardContent className="website-template-card__content">
        <div className="website-template-card__name-row">
          <h3 title={template.name}>{template.name}</h3>
          {isSelected && (
            <span className="website-template-card__selected">
              <Check className="h-3 w-3" aria-hidden="true" /> Selected
            </span>
          )}
        </div>
        <div className="website-template-card__actions">
          <Button type="button" variant="ghost" size="sm" onClick={onPreview}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Preview
          </Button>
          <Button
            type="button"
            variant={isSelected ? 'secondary' : 'outline'}
            size="sm"
            onClick={onSelect}
            disabled={isSelected || selectionDisabled}
            aria-describedby={selectionDisabled ? selectionDescriptionId : undefined}
          >
            {isSelected ? 'Selected' : 'Select'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
